/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../lib/@waca/core-client/js/core-client/ui/core/Events'
], ( UniqueId, Base) => {
	return class Events extends Base {

		on(eventName, handler, context) {
			const returnValue = super.on(eventName, handler, context);
			const newHandlerObjectIndex = this._events[eventName].length - 1;
			const handlerObject = this._events[eventName][newHandlerObjectIndex];
			handlerObject.id = UniqueId.get('__api_eventHandler_');
			handlerObject.eventName = eventName;
			return returnValue;
		}

		setTransaction(transaction){
			if (!this.transaction){
				this.transaction = transaction;
			}
		}

		/**
		 * Override the glass events to support multiple namespaces.
		 * For example. For the event "change:content:property:suppression"
		 * i can list for the following events:
		 *
		 * "change"
		 * "change:content"
		 * "change:content:property"
		 * "change:content:property:suppression"
		 *
		 * The glass events only supports listening for the first and last
		 */
		trigger(eventName, event) {
			var handlers = [].concat(this._events['all'] || []);

			// These handlers are registered on a namespace and not the actual eventName
			// E.g the eventName is 'change:content:visualization:slots:dataItem:sort'
			// We could listen for this specific event, or we could listen for the namespace 'change:content:visualization'.
			// When listening on a namespace, we will pool calls together such that the handler is called once per transaction.
			let namespaceHandlers = [];

			var parts = eventName.split(':');
			if (parts.length > 1) {
				var namespaces = [];
				parts.forEach(part => {
					let value = part;
					if(namespaces.length >  0){
						value = namespaces[namespaces.length - 1] + ':' + part;
					}
					namespaces.push(value);
				});

				namespaces.forEach((namespace) => {
					if (this._events[namespace]) {
						if (namespace === eventName) {
							handlers.push.apply(handlers, this._events[namespace]);
						} else {
							namespaceHandlers.push.apply(namespaceHandlers, this._events[namespace]);
						}
					}
				});
			} else if (this._events[eventName]) {
				handlers.push.apply(handlers, this._events[eventName]);
			}

			for ( let i = 0; i < handlers.length; i++) {
				if (typeof handlers[i].handler === 'function') {
					try {
						handlers[i].handler.call(handlers[i].context, event, eventName);
					} catch(error) {
						console.error('Error when trigger event:' + eventName, error);
					}
				}
			}

			if (!(this.transaction && event.transactionToken)){
				for ( let i = 0; i < namespaceHandlers.length; i++) {
					if (typeof namespaceHandlers[i].handler === 'function') {
						namespaceHandlers[i].handler.call(namespaceHandlers[i].context, event, eventName);
					}
				}
			} else {
				for ( let i = 0; i < namespaceHandlers.length; i++) {
					if (typeof namespaceHandlers[i].handler === 'function') {
						const id = namespaceHandlers[i].id;
						const eventName = namespaceHandlers[i].eventName;
						this.transaction.registerTransactionHandler(event.transactionToken, id, (eventList) => {
							const handler = namespaceHandlers[i].handler;
							if(this._events[eventName].find(event => event.handler === handler)) {
								// Calling the handler was delayed in this branch so we made sure the event was not removed in the meantime. 
								// e.g Same transaction changes and removes a visualization, the change handler could throw if it assumes a visualization.
								handler.call(namespaceHandlers[i].context, {
									name: eventName,
									info:{
										events: eventList
									},
									transactionToken: eventList[0].transactionToken
								}, eventList);
							}
						}, event);
					}
				}
			}
		}
	};
});
